import type { EditorView } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import { syntaxTree } from "@codemirror/language";
import {
  toggleHeading,
  toggleBold,
  toggleItalic,
  toggleBulletList,
  toggleOrderedList,
  toggleCheckbox,
  toggleInlineCode,
  toggleCodeBlock,
  toggleBlockquote,
  insertLink,
  insertHorizontalRule,
} from "./commands";
import { insertWikilink } from "./wikilinks";

/**
 * Maps data-command attribute values to their command functions.
 */
const commandMap: Record<string, (view: EditorView) => void> = {
  "heading-1": (view) => toggleHeading(view, 1),
  "heading-2": (view) => toggleHeading(view, 2),
  "heading-3": (view) => toggleHeading(view, 3),
  bold: toggleBold,
  italic: toggleItalic,
  "bullet-list": toggleBulletList,
  "ordered-list": toggleOrderedList,
  checkbox: toggleCheckbox,
  "inline-code": toggleInlineCode,
  "code-block": toggleCodeBlock,
  blockquote: toggleBlockquote,
  link: insertLink,
  "horizontal-rule": insertHorizontalRule,
  wikilink: insertWikilink,
};

/**
 * Attach a delegated click listener on the toolbar container.
 * Reads `data-command` from the clicked button and dispatches
 * the corresponding command, then returns focus to the editor.
 */
export function setupToolbar(toolbar: HTMLElement, view: EditorView): void {
  toolbar.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const button = target.closest("button[data-command]") as HTMLElement | null;
    if (!button) return;

    const command = button.getAttribute("data-command");
    if (!command) return;

    const handler = commandMap[command];
    if (handler) {
      e.preventDefault();
      handler(view);
      view.focus();
    }
  });
}

/**
 * Node name sets used for detecting active formatting states
 * in the Lezer markdown syntax tree.
 */
const BOLD_NODES = new Set(["StrongEmphasis"]);
const ITALIC_NODES = new Set(["Emphasis"]);
const INLINE_CODE_NODES = new Set(["InlineCode"]);
const CODE_BLOCK_NODES = new Set(["FencedCode", "CodeBlock"]);
const BLOCKQUOTE_NODES = new Set(["Blockquote"]);
const HEADING_NODES: Record<string, number> = {
  ATXHeading1: 1,
  ATXHeading2: 2,
  ATXHeading3: 3,
  SetextHeading1: 1,
  SetextHeading2: 2,
};
const LIST_NODES = new Set(["BulletList"]);
const ORDERED_LIST_NODES = new Set(["OrderedList"]);

/**
 * Update `.active` class on toolbar buttons based on the formatting
 * state at the current cursor position.
 */
export function updateToolbarState(toolbar: HTMLElement, state: EditorState): void {
  const pos = state.selection.main.head;
  const tree = syntaxTree(state);

  // Determine active states by walking ancestors at cursor position
  const active: Record<string, boolean> = {
    bold: false,
    italic: false,
    "inline-code": false,
    "code-block": false,
    blockquote: false,
    "heading-1": false,
    "heading-2": false,
    "heading-3": false,
    "bullet-list": false,
    "ordered-list": false,
    checkbox: false,
  };

  // Walk up the syntax tree from cursor position
  let node: ReturnType<typeof tree.resolveInner> | null = tree.resolveInner(pos, -1);
  while (node) {
    const name = node.name;

    if (BOLD_NODES.has(name)) active["bold"] = true;
    if (ITALIC_NODES.has(name)) active["italic"] = true;
    if (INLINE_CODE_NODES.has(name)) active["inline-code"] = true;
    if (CODE_BLOCK_NODES.has(name)) active["code-block"] = true;
    if (BLOCKQUOTE_NODES.has(name)) active["blockquote"] = true;
    if (LIST_NODES.has(name)) active["bullet-list"] = true;
    if (ORDERED_LIST_NODES.has(name)) active["ordered-list"] = true;

    if (name in HEADING_NODES) {
      const level = HEADING_NODES[name];
      active[`heading-${level}`] = true;
    }

    // Stop at top-level document node
    if (node.type.isTop) break;
    node = node.parent;
  }

  // Detect checkbox: check if current line text starts with "- [ ] " or "- [x] "
  const line = state.doc.lineAt(pos);
  if (/^- \[[ x]\] /.test(line.text)) {
    active["checkbox"] = true;
  }

  // Apply active class to buttons
  const buttons = toolbar.querySelectorAll<HTMLElement>("button[data-command]");
  for (const button of buttons) {
    const command = button.getAttribute("data-command");
    if (command && active[command]) {
      button.classList.add("active");
    } else {
      button.classList.remove("active");
    }
  }
}
